import React from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ChartBarIcon,
  EyeIcon,
  ArrowTrendingUpIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  AcademicCapIcon,
  ChartPieIcon,
  CogIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const ProgramHealthScoreCard = ({
  healthCheckData,
  allProgramsData = [],
  analytics = {},
  userRole,
  onScoreClick,
  onCategoryClick,
}) => {
  const { overallScore = 0 } = healthCheckData;
  const healthStatus = getHealthStatus(overallScore);

  const categoryScores = [
    {
      name: 'Program Design & Structure',
      key: 'programDesign',
      score: healthCheckData.programDesign?.score || 0,
      color: 'blue',
      icon: BuildingOffice2Icon,
    },
    {
      name: 'Entrepreneur Profiling & Assessment',
      key: 'entrepreneurProfiling',
      score: healthCheckData.entrepreneurProfiling?.score || 0,
      color: 'green',
      icon: UserGroupIcon,
    },
    {
      name: 'Curriculum & Program Delivery',
      key: 'curriculumDelivery',
      score: healthCheckData.curriculumDelivery?.score || 0,
      color: 'purple',
      icon: AcademicCapIcon,
    },
    {
      name: 'Monitoring & Evaluation',
      key: 'monitoringEvaluation',
      score: healthCheckData.monitoringEvaluation?.score || 0,
      color: 'orange',
      icon: ChartPieIcon,
    },
    {
      name: 'Support Ecosystem & Operations',
      key: 'supportEcosystem',
      score: healthCheckData.supportEcosystem?.score || 0,
      color: 'indigo',
      icon: CogIcon,
    },
    {
      name: 'Governance, Inclusion & Risk',
      key: 'governanceInclusion',
      score: healthCheckData.governanceInclusion?.score || 0,
      color: 'red',
      icon: ShieldCheckIcon,
    },
  ];

  const canViewAll = userRole === 'admin' || userRole === 'moderator';

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6 shadow-sm"
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Program Health Overview</h2>
            <p className="text-gray-600">
              Comprehensive assessment of program effectiveness and scalability
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Last Updated</div>
            <div className="text-base font-semibold text-gray-900">
              {healthCheckData.lastUpdated
                ? new Date(healthCheckData.lastUpdated).toLocaleDateString()
                : 'Not assessed'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Overall Health Score</h3>
              <healthStatus.icon className={`h-8 w-8 text-${healthStatus.color}-600`} />
            </div>

            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-3xl font-bold text-gray-900">{overallScore}%</span>
                <span
                  className={`rounded-full px-3 py-1 text-sm font-medium bg-${healthStatus.color}-100 text-${healthStatus.color}-800`}
                >
                  {healthStatus.status}
                </span>
              </div>
              <div className="h-3 w-full rounded-full bg-gray-200">
                <div
                  className={`h-3 rounded-full bg-${healthStatus.color}-500 transition-all duration-500`}
                  style={{ width: `${overallScore}%` }}
                />
              </div>
            </div>

            <div className="text-sm text-gray-600">{getScoreDescription(overallScore)}</div>

            {userRole === 'admin' || userRole === 'coach' ? (
              <button
                onClick={onScoreClick}
                className="mt-4 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                <ChartBarIcon className="h-4 w-4" />
                View Detailed Assessment
              </button>
            ) : null}
          </div>

          {canViewAll && (
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Program Distribution</h3>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-gray-700">Healthy & Scalable</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {analytics.healthyPrograms || 0} ({analytics.healthDistribution?.healthy || 0}%)
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ClockIcon className="h-5 w-5 text-yellow-600" />
                    <span className="text-sm text-gray-700">Emerging Maturity</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {analytics.emergingPrograms || 0} ({analytics.healthDistribution?.emerging || 0}
                    %)
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />
                    <span className="text-sm text-gray-700">At Risk</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {analytics.atRiskPrograms || 0} ({analytics.healthDistribution?.atRisk || 0}%)
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircleIcon className="h-5 w-5 text-red-600" />
                    <span className="text-sm text-gray-700">Critical Gaps</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {analytics.criticalPrograms || 0} ({analytics.healthDistribution?.critical || 0}
                    %)
                  </span>
                </div>
              </div>

              <div className="mt-4 rounded-lg bg-gray-50 p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Average Health Score</span>
                  <span className="font-semibold text-gray-900">
                    {analytics.avgHealthScore || 0}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-6 text-xl font-semibold text-gray-900">Assessment Categories</h3>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {categoryScores.map((category, index) => (
            <motion.div
              key={category.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-4 transition-shadow hover:shadow-md"
              onClick={() => onCategoryClick(category.key)}
            >
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <category.icon className={`h-6 w-6 text-${category.color}-600`} />
                  <span className="text-sm font-medium text-gray-900">{category.name}</span>
                </div>
                <EyeIcon className="h-4 w-4 text-gray-400" />
              </div>

              <div className="mb-2">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-lg font-bold text-gray-900">{category.score}%</span>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-medium bg-${category.color}-100 text-${category.color}-800`}
                  >
                    {getScoreStatus(category.score)}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-200">
                  <div
                    className={`h-2 rounded-full bg-${category.color}-500 transition-all duration-500`}
                    style={{ width: `${category.score}%` }}
                  />
                </div>
              </div>

              <div className="text-xs text-gray-600">Click to view detailed assessment</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {canViewAll && allProgramsData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <h3 className="mb-6 text-xl font-semibold text-gray-900">All Programs Health Status</h3>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Program ID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Health Score</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Last Updated</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allProgramsData.map(program => {
                  const programStatus = getHealthStatus(program.overallScore || 0);
                  return (
                    <tr key={program.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{program.id}</td>
                      <td className="px-4 py-3">
                        <span className="text-lg font-bold text-gray-900">
                          {program.overallScore || 0}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-${programStatus.color}-100 text-${programStatus.color}-800`}
                        >
                          <programStatus.icon className="h-3 w-3" />
                          {programStatus.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {program.lastUpdated
                          ? new Date(program.lastUpdated).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => onCategoryClick('programDesign')}
                          className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  );
};

const getHealthStatus = score => {
  if (score >= 81) return { status: 'Healthy & Scalable', color: 'green', icon: CheckCircleIcon };
  if (score >= 61) return { status: 'Emerging Maturity', color: 'yellow', icon: ClockIcon };
  if (score >= 41) return { status: 'At Risk', color: 'orange', icon: ExclamationTriangleIcon };
  return { status: 'Critical Gaps', color: 'red', icon: XCircleIcon };
};

const getScoreStatus = score => {
  if (score >= 81) return 'Healthy';
  if (score >= 61) return 'Emerging';
  if (score >= 41) return 'At Risk';
  return 'Critical';
};

const getScoreDescription = score => {
  if (score >= 81) {
    return 'Strong entry-to-exit structure with measurable impact and best practices alignment.';
  }
  if (score >= 61) {
    return 'Operational program with good foundation, needs refinement in tracking and support.';
  }
  if (score >= 41) {
    return 'Basic program logic exists but lacks consistency or strategic integration.';
  }
  return 'Program lacks foundational design or delivery mechanisms.';
};

export default ProgramHealthScoreCard;
